import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["buttonText", "buttonUse", "buttonSize", "buttonStyles", "children", "closeOnMenuClick", "disabled", "Content", "icon", "locked", "menuWidth", "onOpenChange", "onOpenComplete", "popoverProps", "use", "open", "isDropdownSelect", "dropdownStyles", "shouldCloseOnClick"];
import { useState, useRef, useEffect, Children, isValidElement, cloneElement, useCallback, useMemo } from 'react';
import { useTheme } from 'foundations-theming/utils';
import { uniqueId } from '../../utils/uniqueId';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { StyledButton, StyledButtonTextContainer, UnstyledButton } from '../Button/PrivateButton';
import { CheckboxWrapper } from '../Checkbox/Checkbox';
import { closest } from '../../utils/dom';
import { moveFocusToNext } from '../../utils/dom/dom';
import getComponentName from 'react-utils/getComponentName';
import { AbstractDropdownWithSearchbox } from '../AbstractDropdown';
import { DropdownContextProvider } from './DropdownContext';
import { Controllable } from '../Controllable';
import { InnerTextSpan } from '../shared-components/ToggleInputWrapper';
import { Radio } from '../RadioInput/RadioInput';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { isLinkUse } from '../Button/Button.helpers';
import { ButtonLabel } from './internal/DropdownMenuOption';
import { ControlProvider } from '../Link/ControlContext';
import { SettingsIcon } from 'foundations-assets/icons/components/SettingsIcon';
import { useIsKeyboardMode } from '../../../components/AppProvider/KeyboardModeProvider';
import { jsx as _jsx } from "react/jsx-runtime";
export const UncontrolledDropdown = _ref => {
  let {
      buttonText,
      buttonUse = 'tertiary-light',
      buttonSize = 'medium',
      buttonStyles,
      children,
      closeOnMenuClick = true,
      disabled = false,
      Content,
      icon,
      locked,
      menuWidth = 'auto',
      onOpenChange,
      onOpenComplete,
      popoverProps,
      use = 'list',
      open,
      isDropdownSelect = false,
      dropdownStyles,
      shouldCloseOnClick
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [state, setState] = useState({
    enableAutofocus: false,
    hasSearchbox: false
  });
  const theme = useTheme();
  const isKeyboardMode = useIsKeyboardMode();
  const menuRef = useRef(null);
  const menuId = useRef(`dropdown-menu-${uniqueId()}`);
  const checkForSearchbox = useCallback(() => {
    const menuEl = menuRef.current;
    if (menuEl) {
      const hasSearchbox = !!menuEl.querySelector('[role="combobox"]');
      if (hasSearchbox !== state.hasSearchbox) {
        setState(prev => Object.assign({}, prev, {
          hasSearchbox
        }));
      }
    }
  }, [state.hasSearchbox]);

  // Check for searchbox when menu ref is set
  useEffect(() => {
    checkForSearchbox();
  }, [checkForSearchbox]);
  const menuRefCallback = useCallback(ref => {
    menuRef.current = ref;
    checkForSearchbox();
  }, [checkForSearchbox]);
  const nodeDisplayNameMatches = (node, regex) => !!(node && /*#__PURE__*/isValidElement(node) && getComponentName(node.type).match(regex));
  const focusNextMenuItem = (reverse = false) => {
    const menu = document.getElementById(menuId.current);
    if (menu) {
      return moveFocusToNext('li', menu, reverse);
    }
    return false;
  };
  const handleOpenComplete = () => {
    if (!(state.hasSearchbox || use === 'unstyled') && isKeyboardMode) {
      // Set focus on the first item when the dropdown opens via keyboard
      focusNextMenuItem();
    }
    onOpenComplete === null || onOpenComplete === void 0 || onOpenComplete();
  };
  const handleKeyDown = evt => {
    const {
      key
    } = evt;
    if (!open) {
      if (key === 'Enter' || key === ' ') {
        evt.preventDefault();
        evt.stopPropagation();
        onOpenChange === null || onOpenChange === void 0 || onOpenChange(syntheticEvent(true));
      }
      return;
    }
    if (state.hasSearchbox) {
      if (key === 'Enter') {
        onOpenChange === null || onOpenChange === void 0 || onOpenChange(syntheticEvent(false));
      }
      evt.stopPropagation();
      return;
    }

    // Move the focus to the next/prev menu item (<li>)
    if (use !== 'unstyled' && (key === 'ArrowDown' || key === 'ArrowUp')) {
      if (focusNextMenuItem(key === 'ArrowUp')) {
        evt.preventDefault();
      }
      evt.stopPropagation();
    }
    if (key === 'Enter' || key === ' ') {
      evt.preventDefault();
      evt.stopPropagation();

      // If a menu item is focused, trigger its click handler
      const activeElement = document.activeElement;
      if (activeElement && activeElement.tagName === 'BUTTON') {
        activeElement.click();
      }
      onOpenChange === null || onOpenChange === void 0 || onOpenChange(syntheticEvent(false));
    }
  };
  const handleMenuClick = useCallback(evt => {
    // If `shouldCloseOnClick` is provided and returns a non-null value, defer to it
    if (shouldCloseOnClick) {
      const shouldClose = shouldCloseOnClick(evt);
      if (shouldClose) {
        onOpenChange === null || onOpenChange === void 0 || onOpenChange(syntheticEvent(false));
        return;
      }
    }
    if (closeOnMenuClick) {
      const {
        target
      } = evt;
      if (!closest(target, '[data-dropdown-menu]')) return;
      let iterableTarget = target;
      while (iterableTarget && !iterableTarget.hasAttribute('data-dropdown') && iterableTarget.getAttribute('data-component-name') !== 'FileButton') {
        if ((['BUTTON', 'A'].includes(iterableTarget.nodeName) || iterableTarget.getAttribute('data-uic-allow-dropdown-close') === 'true') && !iterableTarget.disabled) {
          onOpenChange === null || onOpenChange === void 0 || onOpenChange(syntheticEvent(false));
          return;
        }
        iterableTarget = iterableTarget.parentElement;
      }
    }
  }, [closeOnMenuClick, onOpenChange, shouldCloseOnClick]);
  const containsFooter = Children.toArray(children).some(child => nodeDisplayNameMatches(child, /DropdownFooter/));
  const buttonStyleOverrides = useMemo(() => (buttonSize === 'medium' || buttonSize === 'md') && !isLinkUse(buttonUse) ? {
    paddingInline: buttonUse === 'transparent' ? theme.space['300'] : buttonUse === 'form' ? `${theme.space[250]} ${theme.space[400]}` : theme.space['600']
  } : {}, [buttonSize, buttonUse, theme.space]);
  const dropdownStyleOverrides = useMemo(() => Object.assign({
    paddingBlock: isDropdownSelect || containsFooter ? theme.space['0'] : theme.space['200'],
    // Note:
    // a link element does not support disabled attribute, while a button does so wee need to check for both disabled & aria-disabled attributes
    // targeting button and UnstyledButton/StyledButton is repetitive so we only target button

    [`&&& li`]: {
      // default styles for all links and buttons
      '& > a, & > button, & > span > button': {
        backgroundColor: theme.color['fill-surface-default'],
        lineHeight: theme.typography['body-300'].lineHeight,
        fontSmoothing: 'auto',
        fontSize: theme.typography['body-100'].fontSize,
        fontWeight: theme.typography['body-300'].fontWeight,
        userSelect: 'none',
        border: 0,
        borderRadius: theme.borderRadius['0'],
        color: theme.color['text-core-default'],
        display: 'block',
        minHeight: '40px',
        paddingBlock: theme.space['200'],
        paddingInline: theme.space['500'],
        textAlign: 'left',
        width: '100%',
        // no options in Dropdown should have underline
        textDecoration: 'none'
      },
      // This targets disabled children
      '&[aria-disabled="true"] > *, & > a[aria-disabled="true"], & > button[aria-disabled="true"], & > button[disabled]': {
        color: theme.color['text-core-disabled'],
        cursor: 'not-allowed'
      },
      // This targets link/button that is not disabled
      '& > a:not([aria-disabled="true"]), & > button:not([aria-disabled="true"]):not([disabled]), & > span > button:not([aria-disabled="true"]):not([disabled])': {
        '&:hover': {
          backgroundColor: theme.color['fill-field-hover']
        },
        '&:focus:not(:hover)': {
          backgroundColor: theme.color['fill-field-hover']
        },
        '&.selected:not(:hover)': {
          backgroundColor: theme.color['fill-field-hover']
        }
      }
    },
    // when a button is used a label, there's a nested button (li > Button > DropdownMenuOption > Button)
    // this makes sure that the inner most button's styles are stripped to make it look like other options
    [`&&& ${ButtonLabel}`]: {
      '& > button': {
        padding: theme.space['0'],
        color: 'inherit' // inherits color of parent button (li > Button styled above)
      }
    },
    [`& ${StyledButtonTextContainer}`]: {
      justifyContent: 'flex-start'
    }
  }, disabled && {
    [`& ${StyledButton}, & ${UnstyledButton}`]: {
      backgroundColor: `${theme.color['fill-transparent-default']} !important`
    }
  }, {
    [`& ${CheckboxWrapper}`]: {
      minHeight: '40px',
      paddingInline: theme.space['500'],
      [`& ${InnerTextSpan}`]: {
        paddingInlineStart: '23px' // no token
      },
      '&:hover, &:has(input:checked)': {
        backgroundColor: theme.color['fill-field-hover'],
        color: theme.color['text-core-default']
      }
    },
    [`& ${Radio}`]: {
      minHeight: '40px',
      paddingInlineStart: theme.space['600'],
      paddingInlineEnd: theme.space['500']
    }
    // TODO: Add this when Searchable Select Input is ready

    // '&.private-searchable-select-input__dropdown': {
    //   paddingBlockStart: 0,
    // },
  }), [isDropdownSelect, containsFooter, theme.space, theme.color, theme.typography, theme.borderRadius, disabled]);

  // Store current values in refs for stable renderContent function
  const childrenRef = useRef(children);
  const ContentRef = useRef(Content);
  childrenRef.current = children;
  ContentRef.current = Content;

  // Stable function reference like original class method
  const RenderedContent = useCallback(() => {
    let mutatedChildren = childrenRef.current;
    if (state.hasSearchbox && !ContentRef.current) {
      mutatedChildren = Children.map(childrenRef.current, child => {
        if ( /*#__PURE__*/isValidElement(child) && nodeDisplayNameMatches(child, /Typeahead|GroupedTypeahead/)) {
          return /*#__PURE__*/cloneElement(child, {
            autoFocus: state.enableAutofocus ? true : child.props.autoFocus
          });
        }
        return child;
      });
    }
    return /*#__PURE__*/_jsx("div", {
      "data-dropdown-menu": true,
      id: menuId.current,
      onClick: handleMenuClick,
      ref: menuRefCallback,
      role: "presentation",
      children: ContentRef.current ? /*#__PURE__*/_jsx(ContentRef.current, {}) : mutatedChildren
    });
  }, [state.hasSearchbox, state.enableAutofocus, handleMenuClick, menuRefCallback]);
  const defaultIcon = !buttonText && icon === undefined ? SettingsIcon : undefined;
  const buttonCssStyles = useMapTokenPropsToCSS(buttonStyles || {});
  const contextValue = useMemo(() => ({
    isDropdown: true,
    shouldLogModalWarning: true,
    dropdownStyleOverrides,
    buttonStyleOverrides
  }), [dropdownStyleOverrides, buttonStyleOverrides]);
  return /*#__PURE__*/_jsx(ControlProvider, {
    isControl: true,
    children: /*#__PURE__*/_jsx(DropdownContextProvider, {
      value: contextValue,
      children: /*#__PURE__*/_jsx(AbstractDropdownWithSearchbox, Object.assign({
        open: open,
        buttonText: buttonText,
        buttonUse: buttonUse,
        buttonSize: buttonSize,
        buttonStyles: buttonCssStyles,
        Content: RenderedContent,
        disabled: disabled,
        menuWidth: menuWidth,
        hasSearchbox: state.hasSearchbox,
        icon: icon !== undefined ? icon : defaultIcon,
        locked: locked,
        onOpenComplete: handleOpenComplete,
        onKeyDown: handleKeyDown,
        popoverProps: popoverProps,
        onOpenChange: onOpenChange,
        shouldCloseOnClick: shouldCloseOnClick,
        use: use,
        dropdownStyles: dropdownStyles
      }, rest, {
        children: children
      }))
    })
  });
};
export const Dropdown = Controllable(UncontrolledDropdown, ['open']);
Dropdown.displayName = 'Dropdown';